import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    BookOpen,
    Menu,
    X,
    ArrowLeft,
    ArrowRight,
    Lock,
    ShoppingBag,
    CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STRIPE_LINKS = {
    "module-1": "https://buy.stripe.com/7sYbJ14Q305E8szc6Y1kA01",
    "module-2": "https://buy.stripe.com/bJeeVdeqDbOmfV1b2U1kA02",
    "module-3": "https://buy.stripe.com/6oUdR94Q319I1077QI1kA03",
    "module-4": "https://buy.stripe.com/9B614n4Q33hQ6kr3As1kA04",
    "module-5": "https://buy.stripe.com/fZu3cv82f6u24cjef61kA09",
    "module-6": "https://buy.stripe.com/6oUfZh5U7bOmeQX7QI1kA0a",
};

const MODULE_TITLE = {
    "module-1": "Module 1",
    "module-2": "Module 2",
    "module-3": "Module 3",
    "module-4": "Module 4",
    "module-5": "Module 5",
    "module-6": "Module 6",
};

const MODULE_PRICE = {
    "module-1": 99,
    "module-2": 99,
    "module-3": 99,
    "module-4": 129,
    "module-5": 129,
    "module-6": 149,
};

const kindLabel = {
    welcome: "Overview",
    lesson: "Lesson",
    worksheets: "Worksheets",
    prompts: "Prompt library",
    "answer-key": "Answer key",
};

export default function ModulePlayer() {
    const { moduleId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [module, setModule] = useState(null);
    const [state, setState] = useState({ phase: "loading" });
    const [activeId, setActiveId] = useState(null);
    const [navOpen, setNavOpen] = useState(false);
    const [visited, setVisited] = useState(new Set());

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setState({ phase: "loading" });
            try {
                const { data } = await api.get(`/modules/${moduleId}`);
                if (cancelled) return;
                setModule(data);
                setActiveId(data.pages?.[0]?.id || null);
                setVisited(new Set(data.pages?.[0]?.id ? [data.pages[0].id] : []));
                setState({ phase: "ready" });
            } catch (e) {
                if (cancelled) return;
                const status = e?.response?.status;
                if (status === 401) setState({ phase: "unauthorized" });
                else if (status === 403) setState({ phase: "not-owned" });
                else if (status === 404) setState({ phase: "not-found" });
                else setState({ phase: "error", message: e?.response?.data?.detail || e.message });
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [moduleId]);

    const pages = module?.pages || [];
    const activeIndex = useMemo(
        () => pages.findIndex((p) => p.id === activeId),
        [pages, activeId],
    );
    const activePage = activeIndex >= 0 ? pages[activeIndex] : null;

    const goTo = (id) => {
        setActiveId(id);
        setVisited((prev) => new Set(prev).add(id));
        setNavOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    };
    const prev = () => activeIndex > 0 && goTo(pages[activeIndex - 1].id);
    const next = () => activeIndex < pages.length - 1 && goTo(pages[activeIndex + 1].id);

    if (state.phase === "loading") {
        return (
            <div
                data-testid="player-loading"
                className="min-h-[60vh] flex items-center justify-center text-sm text-muted-foreground"
            >
                Loading module…
            </div>
        );
    }

    if (state.phase === "unauthorized") {
        // Fallback in case ProtectedRoute misses it
        navigate(`/login?next=/learn/${moduleId}`);
        return null;
    }

    if (state.phase === "not-owned") {
        return (
            <div className="container-edge py-24 min-h-[60vh]" data-testid="player-locked">
                <div className="max-w-2xl border border-black/15 p-10 bg-white">
                    <Lock className="text-klein" size={28} />
                    <h1 className="mt-6 font-heading font-black text-3xl tracking-tighter">
                        This module isn't in your library yet.
                    </h1>
                    <p className="mt-3 text-muted-foreground">
                        Signed in as <span className="font-semibold text-foreground">{user?.email}</span>. Purchase
                        {" "}{MODULE_TITLE[moduleId] || "this module"} to unlock the full player — welcome, seven
                        lessons, seven worksheets, the prompt library, and the answer key.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a
                            href={STRIPE_LINKS[moduleId]}
                            data-testid="player-buy-cta"
                            className="inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors"
                        >
                            <ShoppingBag size={16} /> Buy {MODULE_TITLE[moduleId] || "this module"} — ${MODULE_PRICE[moduleId] || 99}
                        </a>
                        <Link
                            to="/dashboard"
                            className="inline-flex items-center gap-2 px-6 py-3 border border-black/20 text-sm font-semibold hover:bg-black hover:text-white transition-colors"
                        >
                            Back to dashboard
                        </Link>
                    </div>
                    <p className="mt-6 text-xs text-muted-foreground">
                        Already purchased? Email{" "}
                        <a className="text-klein underline" href="mailto:gdobson@degreesofsuccessinfo.com">
                            gdobson@degreesofsuccessinfo.com
                        </a>{" "}
                        with your receipt and we'll grant access.
                    </p>
                </div>
            </div>
        );
    }

    if (state.phase === "not-found") {
        return (
            <div className="container-edge py-24 min-h-[60vh]" data-testid="player-not-found">
                <div className="max-w-2xl border border-black/15 p-10 bg-white">
                    <h1 className="font-heading font-black text-3xl tracking-tighter">Content coming soon</h1>
                    <p className="mt-3 text-muted-foreground">
                        This module's content isn't published yet. Modules 2–6 are being finalized and will appear
                        in your dashboard as soon as they're ready.
                    </p>
                    <Link
                        to="/dashboard"
                        className="mt-6 inline-flex items-center gap-2 px-6 py-3 border border-black/20 text-sm font-semibold hover:bg-black hover:text-white transition-colors"
                    >
                        <ArrowLeft size={16} /> Back to dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (state.phase === "error" || !activePage) {
        return (
            <div className="container-edge py-24 min-h-[60vh]" data-testid="player-error">
                <div className="max-w-2xl border border-black/15 p-10 bg-white">
                    <h1 className="font-heading font-black text-3xl tracking-tighter">Couldn't load module</h1>
                    <p className="mt-3 text-muted-foreground text-sm">{state.message || "Please try again."}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-background min-h-screen" data-testid="module-player">
            {/* Sticky module bar */}
            <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-black/10">
                <div className="container-edge py-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <p className="overline">Module Player</p>
                        <h1 className="mt-1 font-heading font-bold text-lg tracking-tight truncate">
                            {module.title}
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:inline text-xs uppercase tracking-widest text-muted-foreground">
                            {activeIndex + 1} / {pages.length}
                        </span>
                        <button
                            type="button"
                            data-testid="player-nav-toggle"
                            onClick={() => setNavOpen((s) => !s)}
                            className="lg:hidden inline-flex items-center gap-2 px-3 py-2 border border-black/20 text-xs font-semibold"
                        >
                            {navOpen ? <X size={14} /> : <Menu size={14} />} Chapters
                        </button>
                    </div>
                </div>
                <div className="h-1 bg-black/5">
                    <div
                        className="h-full bg-klein transition-all"
                        style={{ width: `${((activeIndex + 1) / pages.length) * 100}%` }}
                    />
                </div>
            </div>

            <div className="container-edge py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Sidebar */}
                <aside
                    className={`lg:col-span-4 xl:col-span-3 ${
                        navOpen ? "block" : "hidden"
                    } lg:block`}
                    data-testid="player-sidebar"
                >
                    <nav className="border border-black/15 bg-white sticky lg:top-40">
                        <div className="p-5 border-b border-black/10 flex items-center gap-2">
                            <BookOpen size={16} className="text-klein" />
                            <span className="overline">Chapters</span>
                        </div>
                        <ol className="max-h-[70vh] overflow-y-auto">
                            {pages.map((p, i) => {
                                const isActive = p.id === activeId;
                                const wasVisited = visited.has(p.id);
                                return (
                                    <li key={p.id}>
                                        <button
                                            type="button"
                                            data-testid={`player-chapter-${p.id}`}
                                            onClick={() => goTo(p.id)}
                                            className={`w-full text-left px-5 py-4 border-b border-black/10 last:border-b-0 flex items-start gap-3 transition-colors ${
                                                isActive
                                                    ? "bg-klein text-white"
                                                    : "hover:bg-black/[0.03]"
                                            }`}
                                        >
                                            <span
                                                className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-xs font-mono ${
                                                    isActive
                                                        ? "bg-white text-klein"
                                                        : wasVisited
                                                        ? "bg-klein text-white"
                                                        : "bg-black/5 text-foreground"
                                                }`}
                                            >
                                                {wasVisited && !isActive ? (
                                                    <CheckCircle2 size={14} />
                                                ) : (
                                                    String(i + 1).padStart(2, "0")
                                                )}
                                            </span>
                                            <div className="min-w-0">
                                                <div
                                                    className={`text-[10px] tracking-widest uppercase font-mono ${
                                                        isActive ? "text-white/70" : "text-klein"
                                                    }`}
                                                >
                                                    {kindLabel[p.kind] || "Section"}
                                                </div>
                                                <div className="text-sm font-semibold mt-0.5 leading-snug">
                                                    {p.title}
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </nav>
                </aside>

                {/* Main content */}
                <article
                    className="lg:col-span-8 xl:col-span-9 border border-black/15 bg-white"
                    data-testid="player-article"
                >
                    <header className="px-6 py-8 md:px-12 md:pt-14 md:pb-8 border-b border-black/10">
                        <p className="overline">{activePage.eyebrow || kindLabel[activePage.kind]}</p>
                        <h2
                            data-testid="player-page-title"
                            className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter leading-[0.95]"
                        >
                            {activePage.title}
                        </h2>
                    </header>

                    <div
                        className="px-6 py-8 md:px-12 md:py-12 prose-player"
                        data-testid="player-page-body"
                    >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {activePage.body_md || ""}
                        </ReactMarkdown>
                    </div>

                    <footer className="px-6 py-6 md:px-12 md:py-8 border-t border-black/10 flex items-center justify-between gap-4">
                        <button
                            type="button"
                            onClick={prev}
                            disabled={activeIndex === 0}
                            data-testid="player-prev"
                            className="inline-flex items-center gap-2 px-5 py-3 border border-black/20 text-sm font-semibold hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground"
                        >
                            <ArrowLeft size={16} /> Previous
                        </button>
                        <span className="text-xs uppercase tracking-widest text-muted-foreground hidden sm:inline">
                            {activeIndex + 1} of {pages.length}
                        </span>
                        <button
                            type="button"
                            onClick={next}
                            disabled={activeIndex === pages.length - 1}
                            data-testid="player-next"
                            className="inline-flex items-center gap-2 px-5 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-klein"
                        >
                            Next <ArrowRight size={16} />
                        </button>
                    </footer>
                </article>
            </div>
        </div>
    );
}
